import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { inject, observer } from 'mobx-react'
import { Tooltip, Position } from '@blueprintjs/core'
import throttle from 'lodash/throttle'

import EmojiPicker from '@client/components/EmojiPicker'
import EditorModel, {
  NULL as EditorModelNULL,
} from '@client/components/Editor/model'
import { showDialog as showSaveAsTemplateDialog } from '@client/components/SaveAsTemplateDialog'
import AppModel, { NULL as AppModelNULL } from '@client/components/App/model'
import Preview from '@client/components/Preview/model'
import { errorDialogModel } from '@client/components/ErrorDialog/model'
import { APP_CONFIG_GET_A_NEWS_LETTER_ORIGIN } from '@config/app.config'
import { ITEM_TYPE_FORM } from '@client/components/Editor/settings'
import { getNextUrl } from '@client/components/FooterNav'
import {
  ITEM_TYPE_CONFIRMATION_MAIL,
  ITEM_TYPE_MAIL,
  ITEM_TYPE_RESPONDER,
} from '@client/components/Editor/settings'
import { InboxPreview } from '@client/components/EditSubjectModal'
import {
  TITLE_MAX_LENGTH,
  countCharacters,
  fitsLimit,
  limitEditWithCursor,
  restoreCursor,
} from '@client/utils/textLimit'

import './styles.scss'

@inject('editor', 'app')
@observer
class Header extends Component {
  subjectRef = null
  emojiWrapperRef = null
  emojiPickerToggleRef = null

  static propTypes = {
    editor: PropTypes.instanceOf(EditorModel),
    app: PropTypes.instanceOf(AppModel),
  }

  static defaultProps = {
    editor: EditorModelNULL,
    app: AppModelNULL,
  }

  state = {
    isEditSubject: false,
    isEmojiSelectOpen: false,
    subject: null,
    // An edit or emoji that went past TITLE_MAX_LENGTH was cut (FE-1491).
    subjectEditCut: false,
    headerToolbarBelowXl: false,
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside)
    this.headerToolbarMq = window.matchMedia('(max-width: 1279px)')
    this.setState({ headerToolbarBelowXl: this.headerToolbarMq.matches })
    this.headerToolbarMqListener = () =>
      this.setState({ headerToolbarBelowXl: this.headerToolbarMq.matches })
    this.headerToolbarMq.addEventListener(
      'change',
      this.headerToolbarMqListener
    )
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside)
    if (this.headerToolbarMq && this.headerToolbarMqListener) {
      this.headerToolbarMq.removeEventListener(
        'change',
        this.headerToolbarMqListener
      )
    }
  }

  handleClickOutside = (event) => {
    if (
      this.emojiWrapperRef &&
      !this.emojiWrapperRef.contains(event.target) &&
      this.emojiPickerToggleRef &&
      !this.emojiPickerToggleRef.contains(event.target)
    ) {
      this.setState({
        isEmojiSelectOpen: false,
      })
    }
  }

  getSubjectRef = (el) => {
    this.subjectRef = el
  }

  getEmojiPickerWrapperRef = (el) => {
    this.emojiWrapperRef = el
  }

  getEmojiPickerToggleRef = (el) => {
    this.emojiPickerToggleRef = el
  }

  isSubjectValid = () => {
    const isValid = this.props.editor.isSubjectValid(this.state.subject)
    return isValid
  }

  isSubjectLimitReached = () =>
    this.state.subjectEditCut ||
    countCharacters(this.state.subject) >= TITLE_MAX_LENGTH

  isSubjectTooltipOpen = () => {
    return (
      this.state.isEditSubject &&
      this.state.subject !== null &&
      (!this.isSubjectValid() || this.isSubjectLimitReached())
    )
  }

  toogleEditSubject = (event) => {
    this.setState(
      {
        isEditSubject: !this.state.isEditSubject,
        subjectEditCut: false,
      },
      () => {
        if (this.subjectRef && this.state.isEditSubject) {
          this.subjectRef.focus()
          this.setState({
            subject: this.props.editor.subject,
          })
        }
      }
    )
  }

  // The API rejects more than 255 characters in every field this input saves
  // to (FE-1491), so an edit is only applied as far as that limit allows.
  onSubjectChange = (event) => {
    const { value: subject, cursor } = limitEditWithCursor(
      this.state.subject,
      event.target.value
    )
    this.setState({
      subject,
      subjectEditCut: subject !== event.target.value,
    })
    restoreCursor(event.target, cursor)
  }

  saveSubject = (event) => {
    event.preventDefault()
    event.stopPropagation()
    this.props.editor.setSubject(this.state.subject)
    this.props.editor.save()
    this.setState({
      isEditSubject: !this.state.isEditSubject,
      subject: null,
      subjectEditCut: false,
    })
  }

  saveItem = async (event) => {
    try {
      await this.props.editor.save()
    } catch (e) {
      console.error(e)
    }
  }

  onShowPreviewClick = throttle((event) => {
    this.props.editor.save()
    Preview.setShowPreview(true)
  }, 1000)

  close = (url = `${APP_CONFIG_GET_A_NEWS_LETTER_ORIGIN}/mails/`) => {
    window.location = url
  }

  saveAndClose = (url) => async () => {
    const { editor } = this.props
    try {
      await editor.save()
      // Leaving now would lose edits that only exist in this tab. A rejected
      // save shows the save error banner, a 5xx or a dropped connection the
      // reconnect dialog (FE-1431), and a newer version the save conflict
      // dialog (FE-1441).
      if (editor.lastSaveFailed || editor.saveConflict) {
        return
      }
      this.close(url)
    } catch (e) {
      console.error(e)
    }
  }

  handleSaveAsTemplateDialogClick = () => {
    showSaveAsTemplateDialog()
  }

  handleSaveAsBlockDialogClick = () => {
    showSaveAsTemplateDialog()
  }

  toggleEmojiSelect = () => {
    this.setState({ isEmojiSelectOpen: !this.state.isEmojiSelectOpen })
  }

  handleEmojiSelect = (emoji) => {
    const subject = this.state.subject + emoji.native
    const fits = fitsLimit(subject)
    this.setState({
      ...(fits ? { subject } : {}),
      subjectEditCut: !fits,
      isEmojiSelectOpen: !this.state.isEmojiSelectOpen,
    })
  }

  render() {
    const { editor, app } = this.props
    const { formatMessage } = this.props.app
    const { undo, redo, isUndoDisabled, isRedoDisabled } =
      this.props.app.history

    const { isEditSubject, isEmojiSelectOpen } = this.state

    const isMail =
      editor.kind === ITEM_TYPE_CONFIRMATION_MAIL ||
      editor.kind === ITEM_TYPE_MAIL ||
      editor.kind === ITEM_TYPE_RESPONDER

    const isPreheader = isMail && editor.kind !== ITEM_TYPE_CONFIRMATION_MAIL

    return (
      <nav className="bp6-navbar bp6-dark bp6-fixed-top gan-tw">
        <div className="flex items-center justify-between">
          <div className="bp6-navbar-group">
            <div className="gan-subject">
              {editor.kind !== ITEM_TYPE_FORM && (
                <>
                  <div className="ml1 mr1 bp6-text-muted">
                    {formatMessage(isMail ? 'header.subject' : 'header.title')}
                  </div>
                  <Choose>
                    <When condition={isPreheader}>
                      <InboxPreview
                        editor={editor}
                        formatMessage={formatMessage}
                      />
                    </When>
                    <When condition={isEditSubject && !isPreheader}>
                      <form
                        className="gan-subject__form"
                        onSubmit={this.saveSubject}
                      >
                        <Tooltip
                          className="gan-subject__tooltip"
                          content={
                            <span>
                              {this.isSubjectValid()
                                ? formatMessage('header.limitReached', {
                                    limit: TITLE_MAX_LENGTH,
                                  })
                                : formatMessage(
                                    isMail
                                      ? 'header.subject.tooltip'
                                      : 'header.title.tooltip'
                                  )}
                            </span>
                          }
                          isOpen={this.isSubjectTooltipOpen()}
                          position={Position.BOTTOM}
                        >
                          <div className="bp6-control-group">
                            <div
                              className={`bp6-input-group bp6-fill ${
                                this.isSubjectValid() ? '' : 'bp6-intent-danger'
                              }`}
                            >
                              <input
                                ref={this.getSubjectRef}
                                type="text"
                                className="bp6-input"
                                placeholder={`${formatMessage(
                                  isMail
                                    ? 'header.subject.placeholder'
                                    : 'header.title.placeholder'
                                )}...`}
                                onChange={this.onSubjectChange}
                                value={
                                  this.state.subject === null
                                    ? ''
                                    : this.state.subject
                                }
                              />
                              {isEmojiSelectOpen && (
                                <div
                                  className="gan-subject__emoji-select"
                                  ref={this.getEmojiPickerWrapperRef}
                                >
                                  <EmojiPicker
                                    previewPosition="none"
                                    skinTonePosition="none"
                                    onEmojiSelect={this.handleEmojiSelect}
                                  />
                                </div>
                              )}
                              {isMail && (
                                <span
                                  ref={this.getEmojiPickerToggleRef}
                                  className="bp6-icon gan-subject__emoji-icon"
                                  onClick={this.toggleEmojiSelect}
                                >
                                  <img src="/assets/img/emoji-icon.svg" />
                                </span>
                              )}
                            </div>
                            <button
                              type="submit"
                              className={`bp6-button bp6-intent-${
                                this.isSubjectValid() ? 'primary' : 'danger'
                              }`}
                              disabled={!this.isSubjectValid()}
                            >
                              {formatMessage('header.subject.saveBtn.text')}
                            </button>
                          </div>
                        </Tooltip>
                      </form>
                    </When>
                    <When condition={!isEditSubject && !isPreheader}>
                      <button
                        title={editor.subject}
                        onClick={this.toogleEditSubject}
                        className="bp6-button bp6-minimal bp6-text-overflow-ellipsis gan-subject__text"
                      >
                        <div className="gan-subject__text__label">
                          {editor.subject}
                        </div>
                        <span className="bp6-icon-standard bp6-icon-edit bp6-align-right"></span>
                      </button>
                    </When>
                  </Choose>
                </>
              )}
            </div>
            <Tooltip
              content={formatMessage('header.undoBtn')}
              position={Position.BOTTOM}
              compact
              disabled={!this.state.headerToolbarBelowXl}
            >
              <button
                className="gan-header-responsive-icon-btn bp6-button bp6-minimal bp6-icon-undo"
                onClick={undo}
                disabled={isUndoDisabled}
                aria-label={formatMessage('header.undoBtn')}
              >
                <span className="hidden xl:block">
                  {formatMessage('header.undoBtn')}
                </span>
              </button>
            </Tooltip>
            <Tooltip
              content={formatMessage('header.redoBtn')}
              position={Position.BOTTOM}
              compact
              disabled={!this.state.headerToolbarBelowXl}
            >
              <button
                className="gan-header-responsive-icon-btn bp6-button bp6-minimal bp6-icon-redo whitespace-nowrap"
                onClick={redo}
                disabled={isRedoDisabled}
                aria-label={formatMessage('header.redoBtn')}
              >
                <span className="hidden xl:block">
                  {formatMessage('header.redoBtn')}
                </span>
              </button>
            </Tooltip>
            <span className="bp6-navbar-divider gan-navbar-divider"></span>
            <Choose>
              <When condition={editor.isSaving && !errorDialogModel.error.show}>
                <span className="gan-navbar-save bp6-button bp6-minimal bp6-icon-floppy-disk whitespace-nowrap">
                  {`${formatMessage('header.saving')}...`}
                </span>
              </When>
              <When
                condition={
                  !editor.isSaving &&
                  editor.isSaved &&
                  !errorDialogModel.error.show
                }
              >
                <span className="gan-navbar-save bp6-button bp6-minimal bp6-icon-saved whitespace-nowrap">
                  {formatMessage('header.saved')}
                </span>
              </When>
              <When
                condition={
                  !editor.isSaving &&
                  !editor.isSaved &&
                  editor.saveError &&
                  !errorDialogModel.error.show
                }
              >
                <span className="gan-navbar-save bp6-button bp6-minimal bp6-icon-warning-sign bp6-intent-danger whitespace-nowrap">
                  {formatMessage('header.saveFailed')}{' '}
                </span>
                <button
                  className="bp6-button bp6-minimal whitespace-nowrap"
                  onClick={this.saveItem}
                >
                  {formatMessage('common.saveNowBtn')}
                </button>
              </When>
              <When
                condition={
                  !editor.isSaving &&
                  !editor.isSaved &&
                  !errorDialogModel.error.show
                }
              >
                <span className="gan-navbar-save bp6-button bp6-minimal bp6-icon-saved whitespace-nowrap">
                  {formatMessage('common.notSavedMessage')}{' '}
                </span>
                <button
                  className="bp6-button bp6-minimal whitespace-nowrap"
                  onClick={this.saveItem}
                >
                  {formatMessage('common.saveNowBtn')}
                </button>
              </When>
              <When condition={errorDialogModel.error.show}>
                <span className="gan-navbar-save bp6-button bp6-minimal bp6-icon-warning-sign bp6-intent-danger">
                  {formatMessage('common.connecting')}
                </span>
              </When>
            </Choose>
          </div>
          <div className="bp6-navbar-group">
            <If condition={!this.props.app.hasWizard}>
              <Tooltip
                content={formatMessage('header.saveAsTemplate')}
                position={Position.BOTTOM}
                compact
                disabled={!this.state.headerToolbarBelowXl}
              >
                <button
                  className="gan-header-responsive-icon-btn bp6-button bp6-minimal bp6-icon-add whitespace-nowrap"
                  onClick={this.handleSaveAsTemplateDialogClick}
                  disabled={errorDialogModel.error.show}
                  aria-label={formatMessage('header.saveAsTemplate')}
                >
                  <span className="hidden xl:block">
                    {formatMessage('header.saveAsTemplate')}
                  </span>
                </button>
              </Tooltip>
            </If>
            {/* Only surfaced when there's actually something to review — we
                only validate link *format*, so a persistent button with an
                "all clear" state would falsely imply the links were verified
                to work. */}
            <If condition={editor.linkIssues.length}>
              <Tooltip
                content={formatMessage('header.linkIssues')}
                position={Position.BOTTOM}
                compact
                disabled={!this.state.headerToolbarBelowXl}
              >
                <button
                  className="gan-header-responsive-icon-btn bp6-button bp6-minimal bp6-intent-warning whitespace-nowrap"
                  onClick={editor.toggleLinkIssuesPanel}
                  aria-label={formatMessage('header.linkIssues')}
                  aria-pressed={editor.linkIssuesPanelOpen}
                >
                  <span className="hidden xl:block">
                    {formatMessage('header.linkIssues')}
                  </span>
                  <span className="gan-link-issues-badge">
                    {editor.linkIssues.length}
                  </span>
                </button>
              </Tooltip>
            </If>
            {!this.props.app.hasWizard && (
              <button
                onClick={this.saveAndClose(
                  `${APP_CONFIG_GET_A_NEWS_LETTER_ORIGIN}/mails/drafts/send/${editor.id}/`
                )}
                className="bp6-button bp6-minimal bp6-icon-envelope"
              >
                {formatMessage('header.sendBtn')}
              </button>
            )}
            <span className="bp6-navbar-divider"></span>
            <Tooltip
              content={formatMessage('header.closeBtn')}
              position={Position.BOTTOM}
              compact
              disabled={!this.state.headerToolbarBelowXl}
            >
              <button
                className="gan-header-responsive-icon-btn bp6-button bp6-minimal bp6-icon-small-cross"
                onClick={this.saveAndClose(
                  this.props.app.hasWizard
                    ? getNextUrl(
                        this.props.app.wizard,
                        this.props.editor,
                        this.props.app.returnUrl,
                        this.props.app.returnUrlHash,
                        this.props.app.returnUrlType
                      )
                    : undefined
                )}
                aria-label={formatMessage('header.closeBtn')}
              >
                <span className="hidden xl:block">
                  {formatMessage('header.closeBtn')}
                </span>
              </button>
            </Tooltip>
          </div>
        </div>
      </nav>
    )
  }
}

export default Header
